import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
}

const satisfactionOptions = [
  'Very satisfied',
  'Slightly satisfied',
  'Neutral',
  'Slightly dissatisfied',
  'Very dissatisfied',
];

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [feedback, setFeedback] = useState('');
  const [satisfaction, setSatisfaction] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState('');
  const captchaRef = useRef<ReCAPTCHA>(null);
  const mounted = useRef(true);
  const submitting = useRef(false);
  const successRef = useRef<HTMLDivElement>(null);
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const captchaConfigured = Boolean(
    siteKey &&
      !(
        process.env.NODE_ENV === 'production' &&
        process.env.REACT_APP_ENVIRONMENT !== 'staging' &&
        siteKey === '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'
      ),
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (sent) successRef.current?.focus();
  }, [sent]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting.current) return;
    setError('');
    if (!feedback.trim() || feedback.trim().length > 5000) {
      setFieldError('Enter between 1 and 5,000 characters.');
      return;
    }
    setFieldError('');
    if (!captchaConfigured || !token) {
      setError('Please complete the CAPTCHA verification before sending.');
      return;
    }

    submitting.current = true;
    setPending(true);
    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          feedback: feedback.trim(),
          satisfaction: satisfaction || undefined,
          recaptchaToken: token,
        }),
        signal: AbortSignal.timeout(25000),
      });
      if (response.status === 429) {
        if (mounted.current) setError('Too many attempts. Wait a minute, complete verification, and try again.');
        return;
      }
      const result = await response.json();
      if (!mounted.current) return;
      if (response.ok && result.success === true) {
        setSent(true);
      } else if (response.status === 403) {
        setError('Verification failed. Complete verification again, then resend your feedback.');
      } else {
        throw new Error();
      }
    } catch {
      if (mounted.current) {
        setError(
          'We couldn’t confirm your feedback was saved. Your message is still here. Try again later; retrying may send a duplicate.',
        );
      }
    } finally {
      if (mounted.current) {
        captchaRef.current?.reset();
        setToken(null);
        setPending(false);
      }
      submitting.current = false;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="feedback-title"
      aria-describedby={sent ? 'feedback-description' : 'feedback-privacy'}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      transitionDuration={reducedMotion ? 0 : undefined}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 4 },
          maxHeight: { xs: '100dvh', sm: '90dvh' },
          backgroundImage: 'none',
          pt: { xs: 'env(safe-area-inset-top)', sm: 0 },
        },
      }}
    >
      <Box sx={{ position: 'relative', borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <DialogTitle
          id="feedback-title"
          sx={{ textAlign: 'center', fontSize: '1.25rem', fontWeight: 800, py: 2.5, px: 7 }}
        >
          Give feedback
        </DialogTitle>
        <IconButton
          autoFocus
          aria-label="Close feedback"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            width: 44,
            height: 44,
            bgcolor: 'action.hover',
            color: 'text.primary',
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: { xs: 2.5, sm: 4 }, pb: 'max(24px, env(safe-area-inset-bottom))' }}>
        {sent ? (
          <Box ref={successRef} tabIndex={-1} role="status" sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography component="h3" sx={{ fontWeight: 800, fontSize: '1.5rem', mb: 1 }}>
              Thank you for helping.
            </Typography>
            <Typography id="feedback-description" color="text.secondary" sx={{ mb: 3 }}>
              Your feedback has been saved. You won’t receive a reply, but your input helps us improve the site.
            </Typography>
            <Button variant="contained" onClick={onClose} sx={{ minHeight: 44, px: 5, color: '#17130a' }}>
              Done
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={submit} noValidate aria-busy={pending}>
            <Typography id="feedback-privacy" variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 3 }}>
              Please don’t include names, contact details, children’s information, case numbers, or other sensitive
              information. This form is for website feedback; you won’t receive a reply.
            </Typography>
            <FormControl component="fieldset" disabled={pending} fullWidth sx={{ mb: 3 }}>
              <FormLabel
                component="legend"
                id="feedback-satisfaction-label"
                sx={{ color: 'text.primary', fontWeight: 700, lineHeight: 1.6, mb: 0.5 }}
              >
                How satisfied or dissatisfied are you with the help provided for your issue?
              </FormLabel>
              <Typography id="feedback-satisfaction-help" variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                Choose one (optional).
              </Typography>
              <RadioGroup
                name="satisfaction"
                aria-labelledby="feedback-satisfaction-label"
                aria-describedby="feedback-satisfaction-help"
                value={satisfaction}
                onChange={(event) => setSatisfaction(event.target.value)}
                sx={{ gap: 0.75 }}
              >
                {satisfactionOptions.map((option) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio size="small" />}
                    label={option}
                    sx={{
                      m: 0,
                      minHeight: 44,
                      px: 0.5,
                      border: 1,
                      borderColor: satisfaction === option ? 'primary.main' : 'divider',
                      borderRadius: 2,
                      bgcolor: satisfaction === option ? 'action.selected' : 'transparent',
                      '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
                    }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            <TextField
              id="feedback-message"
              label="Your feedback"
              placeholder="What could work better?"
              multiline
              minRows={5}
              fullWidth
              value={feedback}
              disabled={pending}
              error={Boolean(fieldError) || feedback.trim().length > 5000}
              helperText={fieldError || `${feedback.trim().length.toLocaleString()} / 5,000 characters`}
              inputProps={{ 'aria-describedby': 'feedback-privacy feedback-message-helper-text' }}
              onChange={(event) => {
                setFeedback(event.target.value);
                setFieldError('');
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, mb: 2.5 }}
            />
            <Box sx={{ mb: 2.5 }}>
              {captchaConfigured && siteKey ? (
                <ReCAPTCHA
                  key={theme.palette.mode}
                  ref={captchaRef}
                  sitekey={siteKey}
                  theme={theme.palette.mode}
                  size="compact"
                  onChange={setToken}
                  onExpired={() => {
                    setToken(null);
                    captchaRef.current?.reset();
                    setError('Verification expired. Please complete it again.');
                  }}
                  onErrored={() => {
                    setToken(null);
                    captchaRef.current?.reset();
                    setError('Verification could not load. Check your connection and try again.');
                  }}
                />
              ) : (
                <Alert severity="warning">Feedback is temporarily unavailable. Please try again later.</Alert>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, lineHeight: 1.7 }}>
                We save only your message, satisfaction rating, and submission time. Google reCAPTCHA and our hosting
                provider may process technical data to protect this form.
              </Typography>
            </Box>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={pending || !captchaConfigured}
              startIcon={pending ? <CircularProgress size={18} color="inherit" /> : undefined}
              sx={{ minHeight: 48, color: '#17130a', textTransform: 'none', fontSize: '1rem' }}
            >
              {pending ? 'Sending feedback…' : 'Send feedback'}
            </Button>
            {pending && (
              <Typography role="status" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Waiting for confirmation. If you close this form, your feedback may still be saved.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
